import {
	Banner,
	Button,
	Checkbox,
	FormLayout,
	Heading,
	Modal,
	Stack,
	Tag,
	TextField,
	Tooltip,
} from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGQL } from "../../hooks/useGQL"
import convertCurrency from "../../utils/convertCurrency"
import { serializeIntoCSV } from "../../utils/csvExport.js"
import handleize from "../../utils/handleize"
import CollectionCreator from "../CollectionCreator"
import { CollectionPicker } from "../CollectionPicker.jsx"
import { ConfirmBeforeClosingModal } from "../ConfirmBeforeClosingModal.jsx"
import { CoolButton } from "../CoolButton.jsx"
import VendorField from "../VendorField.jsx"
import { AIOrdersButtons } from "../ai/AIOrdersButtons.jsx"
import { CreditsCostPreview } from "../ai/CreditsCostPreview.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider"
import { DescriptionEditor } from "./DescriptionEditor"
import ProductVariantCheckbox from "./ProductVariantCheckbox"
import { roundPrice } from "../PriceRounding.jsx"
import { ScopesUpdateTutorial } from "../ScopesUpdateTutorial.jsx"

const SingleProductImportModal = (props) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState, setAIState } = useContext(AIStateContext)
	const { t } = useTranslation()

	const [product, setProduct] = useState({})

	const [editedHTML, setEditedHTML] = useState("")
	const [productDescriptionEdited, setProductDescriptionEdited] =
		useState(false)

	const [showCollectionPicker, setShowCollectionPicker] = useState(false)
	const [showCollectionCreator, setShowCollectionCreator] = useState(false)
	const [collectionsSelected, setCollectionsSelected] = useState([])
	const [collectionsCreated, setCollectionsCreated] = useState(0)

	const [variants, setVariants] = useState([])
	const [selectedVariantsIDs, setSelectedVariantsIDs] = useState([])

	const [tags, setTags] = useState(state.defaultTags)
	const [tagString, setTagString] = useState("")

	const [publishChecked, setPublishChecked] = useState(
		state.publishByDefault &&
			state.currentScopes?.includes("write_publications"),
	)
	const [statusActiveChecked, setStatusActiveChecked] = useState(
		state.statusActiveByDefault,
	)
	const [productSubmitted, setProductSubmitted] = useState(false)
	const [handleAlreadyChosen, setHandleAlreadyChosen] = useState(false)

	const [productPrice, setProductPrice] = useState(0)
	const [productCompareAtPrice, setProductCompareAtPrice] = useState(0)

	const [vendor, setVendor] = useState(state.defaultVendor)
	const [taxable, setTaxable] = useState(state.taxableByDefault)

	const [csvLoading, setCsvLoading] = useState(false)
	const [displayedImageIndex, setDisplayedImageIndex] = useState(0)

	const [
		confirmBeforeClosingModalIsOpen,
		setConfirmBeforeClosingModalIsOpen,
	] = useState(false)
	const [showUpdateScopesModal, setShowUpdateScopesModal] = useState(false)

	const gqlQuery = useGQL()

	const resetState = () => {
		setProduct({})

		setEditedHTML("")
		setProductDescriptionEdited(false)

		setShowCollectionPicker(false)
		setShowCollectionCreator(false)
		setCollectionsSelected([])
		setCollectionsCreated(0)

		setVariants([])
		setSelectedVariantsIDs([])

		setTags(state.defaultTags)
		setTagString("")

		setPublishChecked(
			state.publishByDefault &&
				state.currentScopes?.includes("write_publications"),
		)
		setStatusActiveChecked(state.statusActiveByDefault)
		setProductSubmitted(false)
		setHandleAlreadyChosen(false)

		setProductPrice(0)
		setProductCompareAtPrice(0)

		setVendor(state.defaultVendor)
		setTaxable(state.taxableByDefault)

		setCsvLoading(false)
		setDisplayedImageIndex(0)

		setConfirmBeforeClosingModalIsOpen(false)
	}

	const changeTitle = (e) => {
		setProduct({ ...product, title: e, handle: handleize(e) })
		setAIState((state) => ({
			aiOrdersInputs: { ...state.aiOrdersInputs, title: e },
		}))
	}

	const changeHandle = (e) => {
		setProduct({ ...product, handle: handleize(e) })
		setHandleAlreadyChosen(false)
	}

	const changeDescription = (e) => {
		setEditedHTML(e)
		setProductDescriptionEdited(true)
		setAIState((state) => ({
			aiOrdersInputs: { ...state.aiOrdersInputs, description: e },
		}))
	}

	const changePrice = (e) => {
		setProductPrice(e)
		setProduct({
			...product,
			variants: product.variants.map((v) => {
				return { ...v, price: e }
			}),
		})
		setVariants(
			variants.map((v) => {
				return { ...v, price: e }
			}),
		)
	}

	const changeCompareAtPrice = (e) => {
		setProductCompareAtPrice(e)
		setProduct({
			...product,
			variants: product.variants.map((v) => {
				return { ...v, compareAtPrice: e }
			}),
		})
		setVariants(
			variants.map((v) => {
				return { ...v, compareAtPrice: e }
			}),
		)
	}

	const changeVariantPrice = (e, variantID) => {
		setVariants(
			variants.map((v) => {
				return { ...v, price: v.id === variantID ? e : v.price }
			}),
		)
	}

	const changeVariantCompareAtPrice = (e, variantID) => {
		setVariants(
			variants.map((v) => {
				return {
					...v,
					compareAtPrice: v.id === variantID ? e : v.compareAtPrice,
				}
			}),
		)
	}

	const unselectAllVariants = () => {
		setSelectedVariantsIDs([])
	}

	const selectAllVariants = () => {
		setSelectedVariantsIDs(variants.map((v) => v.id))
	}

	const changeSelectedVariants = (e, checked) => {
		if (checked) {
			setSelectedVariantsIDs((prev) => [...prev, e])
		} else {
			setSelectedVariantsIDs((prev) => [...prev].filter((v) => v !== e))
		}
	}

	const addTag = () => {
		setTags([
			...tags,
			...tagString
				.split(",")
				.map((v) => v.trim())
				.filter((v) => v.length > 0),
		])
		setTagString("")
	}

	const removeTag = (tag) => {
		setTags(tags.filter((t) => tag !== t))
	}

	const removeAllTags = () => {
		setTags([])
	}

	const checkValidHandle = async (handle) => {
		setProductSubmitted(true)
		const { data, errors } = await gqlQuery(
			`
			query ProductByHandle($handle: String!) {
				productByHandle(handle: $handle) {
					id
				}
			}
		`,
			{ handle: handle },
		)

		if (data?.productByHandle?.id) {
			return false
		} else if (errors) {
			return false
		} else {
			return true
		}
	}

	const submitProductToUpload = async () => {
		setHandleAlreadyChosen(false)
		let productToUpload = {}

		productToUpload.title = product.title
		productToUpload.descriptionHtml = productDescriptionEdited
			? editedHTML
			: product.body_html
		productToUpload.image = product.image?.src
			? {
					src: product.image.src,
					altText: product.image.alt || "",
					id: product.image.id || "",
				}
			: null
		productToUpload.images = product.images
			?.filter((i) => {
				if (
					i.variant_ids &&
					Array.isArray(i.variant_ids) &&
					i.variant_ids.length > 0
				) {
					return i.variant_ids.some((imgId) => {
						return selectedVariantsIDs.includes(imgId)
					})
				} else {
					return true
				}
			})
			.map((img) => {
				return {
					altText: img.alt || "",
					src: img.src,
					id: img.id,
				}
			})
			.slice(0, 250)

		productToUpload.tags = tags
			.filter((tag) => tag !== "")
			.filter((tag) => tag !== "Kopy" && tag !== "kopy")
			.slice(0, 250)
		productToUpload.variants = variants
			.filter((v) => selectedVariantsIDs.includes(v.id))
			.map((variant) => {
				return {
					price: parseFloat(variant.price),
					options: [
						variant.option1 || "",
						variant.option2 || "",
						variant.option3 || "",
					],
					compareAtPrice: parseFloat(variant.compareAtPrice),
					imageSrc:
						variant.imageSrc ||
						product.images.filter(
							(img) => img.id === variant.image_id,
						)?.[0]?.src ||
						"",
					imageId: variant.image_id,
					_id: variant.id,
					taxable: taxable,
					sku: variant.sku?.toString() || null,
				}
			})
			.slice(0, 250)
		productToUpload.options = product.options.slice(0, 250)

		if (collectionsSelected.length > 0) {
			productToUpload.collectionsToJoin = collectionsSelected.map(
				(selection) => selection.id,
			)
		}
		productToUpload.published = statusActiveChecked && publishChecked
		productToUpload.status = statusActiveChecked ? "ACTIVE" : "DRAFT"
		productToUpload.vendor = vendor
		productToUpload.handle = product.handle

		const handleIsValid = await checkValidHandle(product.handle)
		if (handleIsValid) {
			props.submit(productToUpload, aiState.aiOrders || [])
		} else {
			setHandleAlreadyChosen(true)
			setProductSubmitted(false)
			if (document.querySelector(".Polaris-Modal__Body")) {
				document.querySelector(".Polaris-Modal__Body").scrollTop = 0
			}
		}
	}

	const exportToCSV = () => {
		setCsvLoading(true)

		const { csvUrl, csvFilename } = serializeIntoCSV({
			collectionsSelected,
			products: [product],
			productsUrl: props.productUrl,
			publishChecked: statusActiveChecked && publishChecked,
			status: statusActiveChecked ? "ACTIVE" : "DRAFT",
			selectedProductsIds: [product.id],
			tags,
			vendor,
		})

		setCsvLoading(false)
		props.onCsvExport(csvUrl, csvFilename)
	}

	const changePublishChecked = (change) => {
		if (
			change === true &&
			!state.currentScopes?.includes("write_publications")
		) {
			setShowUpdateScopesModal(true)
		} else {
			setPublishChecked(change)
		}
	}

	const changeActiveStatusChecked = (change) => {
		setStatusActiveChecked(change)
	}

	useEffect(() => {
		return () => {
			resetState()
		}
	}, [])

	useEffect(() => {
		if (props.modalIsOpen === true && props.product) {
			setAIState({
				aiOrdersInputs: {
					title: props.product?.title || "",
					description: props.product?.body_html || "",
					variants: props.product?.variants?.map(
						(v) => v?.title || "",
					),
				},
			})
		}
		if (props.modalIsOpen === false) {
			setAIState({
				aiOrders: [],
				aiOrdersInputs: {
					title: null,
					description: null,
					variants: null,
				},
				aiOrdersTotalCost: 0,
				loadingAiOrdersTotalCost: false,
			})
		}
	}, [props.modalIsOpen])

	useEffect(() => {
		if (props.modalIsOpen === true && props.product) {
			setAIState((state) => ({
				aiOrdersInputs: {
					...state.aiOrdersInputs,
					variants: props.product?.variants
						?.filter((v) => {
							if (v?.id && selectedVariantsIDs.includes(v.id)) {
								return true
							} else {
								return false
							}
						})
						.map((v) => v?.title || ""),
				},
			}))
		}
	}, [selectedVariantsIDs])

	useEffect(() => {
		if (props.modalIsOpen === true && props.product) {
			let productTags = []
			if (!props.product.tags) {
				productTags = [...tags]
			} else if (Array.isArray(props.product.tags)) {
				productTags = [...props.product.tags, ...tags]
			} else if (
				props.product.tags.length > 0 &&
				props.product.tags.split
			) {
				productTags = [...props.product.tags.split(", "), ...tags]
			}

			if (productTags.length > 0 && productTags[0] === "") {
				productTags.shift()
			}

			const deduplicatedProductTags = []
			productTags.forEach((element) => {
				if (!deduplicatedProductTags.includes(element)) {
					deduplicatedProductTags.push(element)
				}
			})

			let convertedPriceVariants = props.product.variants?.map((v) => {
				return {
					...v,
					price:
						state.roundingEnabledByDefault &&
						state.roundingNumberByDefault !== null &&
						v?.price
							? roundPrice(
									convertCurrency(
										parseFloat(v.price),
										props.productCurrency,
										state.shopCurrency,
									),
									state.roundingNumberByDefault,
								).toFixed(2)
							: v?.price
								? convertCurrency(
										parseFloat(v.price),
										props.productCurrency,
										state.shopCurrency,
									).toFixed(2)
								: "",
					compareAtPrice:
						state.roundingEnabledByDefault &&
						state.roundingNumberByDefault !== null &&
						v?.compare_at_price
							? roundPrice(
									convertCurrency(
										parseFloat(v.compare_at_price),
										props.productCurrency,
										state.shopCurrency,
									),
									state.roundingNumberByDefault,
								).toFixed(2)
							: v?.compare_at_price
								? convertCurrency(
										parseFloat(v.compare_at_price),
										props.productCurrency,
										state.shopCurrency,
									).toFixed(2)
								: "",
				}
			})

			const averageProductPrice = (
				convertedPriceVariants.reduce(
					(prev, curr) => prev + parseFloat(curr.price) || 0,
					0,
				) / convertedPriceVariants.length
			).toFixed(2)
			const averageProductCompareAtPrice = (
				convertedPriceVariants.reduce(
					(prev, curr) => prev + parseFloat(curr.compareAtPrice) || 0,
					0,
				) / convertedPriceVariants.length
			).toFixed(2)

			setProduct({
				...props.product,
				variants: convertedPriceVariants,
				handle: handleize(props.product.title),
			})
			setVariants(
				convertedPriceVariants.map((v) => {
					return {
						...v,
						...(v.price === "" && { price: averageProductPrice }),
						...(v.compareAtPrice === "" && {
							compareAtPrice: averageProductCompareAtPrice,
						}),
					}
				}),
			)
			setSelectedVariantsIDs(convertedPriceVariants.map((v) => v.id))
			setTags(deduplicatedProductTags)
			setProductPrice(averageProductPrice)
			setProductCompareAtPrice(averageProductCompareAtPrice)
			if (state.defaultVendor === "") {
				setVendor(props.product.vendor || "")
			}
		} else if (props.modalIsOpen === false) {
			resetState()
		}
	}, [props.modalIsOpen, state.shopCurrency])

	const invalidTitle =
		product?.title?.length === 0 || product?.title?.length > 250
	const invalidVariants = selectedVariantsIDs.length === 0

	const loadingCreditsAvailable =
		aiState.aiOrders?.length && aiState.loadingCreditsAvailable
	const loadingAiOrdersTotalCost =
		aiState.aiOrders?.length && aiState.loadingAiOrdersTotalCost
	const insufficientCredits =
		aiState.aiOrders?.length &&
		aiState.creditsAvailable < aiState.aiOrdersTotalCost
	const importDisabled =
		invalidTitle ||
		handleAlreadyChosen ||
		invalidVariants ||
		loadingCreditsAvailable ||
		loadingAiOrdersTotalCost ||
		insufficientCredits
	const descriptionIsLongEnough =
		(editedHTML || product.body_html)?.trim()?.length > 20
	const hasEnoughVariants = variants?.length > 0
	const hasDefaultTitleVariant = variants?.[0]?.title === "Default Title"

	return (
		<Modal
			large={true}
			open={props.modalIsOpen}
			onClose={() => setConfirmBeforeClosingModalIsOpen(true)}
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
						color: "#333",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1.4rem"
						height="1.4rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M21.329 7.207c0-1.212-.472-2.352-1.329-3.207s-1.996-1.329-3.207-1.329a4.5 4.5 0 0 0-3.18 1.304c-.027.025-7.967 7.964-7.967 7.964c-.373.373-.717.91-.967 1.514c-.195.473-1.979 5.863-2.336 6.939a1 1 0 0 0 1.263 1.263c1.076-.355 6.465-2.141 6.938-2.336c.603-.248 1.14-.592 1.515-.967l2.157-2.156l.076.01c.64 0 1.28-.244 1.769-.732l4.5-4.5a2.487 2.487 0 0 0 .588-2.572c.107-.386.18-.783.18-1.195zM9.465 17.586c-.406.143-1.145.393-2.038.691l-1.704-1.704c.301-.894.551-1.634.691-2.038l3.051 3.051zm-4.097.047l.999.999l-1.498.499l.499-1.498zm7.698-3.113l-2.42 2.42l-.235.18l-3.53-3.529l.18-.234l7.131-7.131l2.731 2.731l-3.69 3.69c-.513.512-.549 1.289-.167 1.873zm6.08-4.959l-4.5 4.5a.502.502 0 0 1-.708 0a.5.5 0 0 1 0-.707l4.5-4.5a.502.502 0 0 1 .707 0a.499.499 0 0 1 .001.707zm.107-1.764a1.489 1.489 0 0 0-1.521.35l-.102.102l-2.731-2.731l.078-.078c.984-.98 2.652-.981 3.608-.023a2.52 2.52 0 0 1 .743 1.793c.001.199-.03.394-.075.587z"
						></path>
					</svg>
					<span>
						{t("single.edit_your_product_before_importing")}
					</span>
				</div>
			}
			primaryAction={{
				content: t("single.import_product"),
				onAction: submitProductToUpload,
				loading: productSubmitted,
				disabled: importDisabled,
			}}
			secondaryActions={[
				{
					content: t("single.export_to_csv"),
					onAction: exportToCSV,
					loading: csvLoading,
					disabled: importDisabled,
				},
			]}
			footer={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					{state.hasValidKopyPlusSubscription ? (
						<CreditsCostPreview context="single" />
					) : (
						""
					)}
					{state.hasValidKopyPlusSubscription &&
					insufficientCredits ? (
						<CoolButton
							onClick={() =>
								setAIState({ isCreditsTopUpOverlayOpen: true })
							}
							innerPadding="0.25rem 0.5rem"
							fontSize="0.85rem"
							outerPadding="0.2rem"
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.2rem",
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="1rem"
									height="1rem"
									viewBox="0 0 24 24"
								>
									<path
										fill="currentColor"
										d="M19 20H5a1 1 0 0 0 0 2h14a1 1 0 0 0 0-2M8.71 7.71L11 5.41V17a1 1 0 0 0 2 0V5.41l2.29 2.3a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42l-4-4a1 1 0 0 0-.33-.21a1 1 0 0 0-.76 0a1 1 0 0 0-.33.21l-4 4a1 1 0 1 0 1.42 1.42"
									></path>
								</svg>
								<span>{t("multiple.top-up_credits")}</span>
							</div>
						</CoolButton>
					) : (
						""
					)}
				</div>
			}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					padding: "1.5rem",
				}}
			>
				<Modal.Section>
					<div style={{ display: "flex" }}>
						<div>
							<img
								className="shadow border"
								style={{
									width: "15rem",
									height: "15rem",
									borderRadius: "1rem",
									marginRight: "1rem",
									objectFit: "contain",
								}}
								src={
									(product?.images &&
										product.images[displayedImageIndex]
											?.src) ||
									""
								}
								referrerPolicy="no-referrer"
							/>
							<div
								style={{
									paddingTop: "0.5rem",
									paddingBottom: "0.5rem",
									display: "flex",
									width: "15rem",
									gap: "0.3rem",
									overflowX: "scroll",
								}}
							>
								{product?.images?.map((img, i) => {
									return (
										<img
											key={`product-image-${i}`}
											referrerPolicy="no-referrer"
											className="shadow border"
											style={{
												width: "3rem",
												height: "3rem",
												borderRadius: "0.6rem",
												objectFit: "contain",
												cursor: "pointer",
												flexShrink: "0",
											}}
											alt=""
											src={img.src || ""}
											onClick={() =>
												setDisplayedImageIndex(i)
											}
										/>
									)
								})}
							</div>
						</div>
						<div
							style={{
								flexGrow: 1,
								display: "flex",
								flexDirection: "column",
								gap: "0.6rem",
							}}
						>
							<TextField
								value={product.title}
								label={t("single.product_title")}
								type="text"
								onChange={changeTitle}
							/>

							{!invalidTitle && (
								<AIOrdersButtons
									size="small"
									inputType="title"
									disabled={
										!state.hasValidKopyPlusSubscription
									}
								/>
							)}

							<div
								className={`invalid-title-error ${invalidTitle ? "open" : "closed"}`}
							>
								<Banner
									title={t("single.invalid_title")}
									status="critical"
								>
									<p>
										{t(
											"single.the_product_title_must_not_be_empty_or_longer_than_250_characters",
										)}
									</p>
								</Banner>
							</div>

							<div>
								<Tooltip
									content={
										<div
											style={{ padding: "0.4rem 0.7rem" }}
										>
											<span
												style={{
													fontSize: "0.9rem",
													color: "#333",
												}}
											>
												{t(
													"single.the_handle_slug_that_will_appear",
												)}
												{product.handle}
											</span>
										</div>
									}
								>
									<p style={{ marginBottom: "0.5rem" }}>
										<span className="info-underline">
											{t("single.product_handle_slug")}
										</span>{" "}
										{t("single.a-z_0-9_and_-")}
									</p>
								</Tooltip>
								<TextField
									value={product.handle}
									type="text"
									onChange={changeHandle}
								/>
							</div>

							<div
								className={`duplicate-handle-error ${handleAlreadyChosen ? "open" : "closed"}`}
							>
								<Banner
									title={t("single.duplicate_handle_error")}
									status="critical"
								>
									<p>{t("single.handle_already_exists")}</p>
								</Banner>
							</div>

							<VendorField
								vendor={vendor}
								onVendorChange={(change) => setVendor(change)}
							/>

							<div>
								<Checkbox
									label={t("single.set_product_as_active")}
									checked={statusActiveChecked}
									onChange={(change) =>
										changeActiveStatusChecked(change)
									}
								/>
							</div>
							<div>
								<Checkbox
									label={t(
										"single.publish_to_online_store_on_import",
									)}
									checked={publishChecked}
									onChange={(change) =>
										changePublishChecked(change)
									}
								/>
							</div>
							<div>
								<Checkbox
									label={t("single.taxable")}
									checked={taxable}
									onChange={() => setTaxable(!taxable)}
								/>
							</div>
						</div>
					</div>
				</Modal.Section>

				<Modal.Section>
					<div style={{ marginBottom: "0.6rem" }}>
						<Heading>{t("single.product_pricing")}</Heading>
					</div>
					<div style={{ display: "flex", gap: "1rem" }}>
						<div style={{ width: "50%" }}>
							<Tooltip
								content={
									<div style={{ padding: "0.4rem 0.7rem" }}>
										<span
											style={{
												fontSize: "0.9rem",
												color: "#333",
											}}
										>
											{t(
												"single.changing_the_product_price",
											)}
										</span>
									</div>
								}
							>
								<p style={{ marginBottom: "0.5rem" }}>
									<span className="info-underline">
										{t("single.average_product_price")}
									</span>
								</p>
							</Tooltip>
							<TextField
								prefix={state.shopCurrency}
								value={productPrice}
								type="number"
								min={0}
								onChange={changePrice}
							/>
						</div>

						<div style={{ width: "50%" }}>
							<Tooltip
								content={
									<div style={{ padding: "0.4rem 0.7rem" }}>
										<span
											style={{
												fontSize: "0.9rem",
												color: "#333",
											}}
										>
											{t(
												"single.changing_the_product_compare_at_price",
											)}
										</span>
									</div>
								}
							>
								<p style={{ marginBottom: "0.5rem" }}>
									<span className="info-underline">
										{t(
											"single.average_product_compare_at_price",
										)}
									</span>
								</p>
							</Tooltip>
							<TextField
								prefix={state.shopCurrency}
								value={productCompareAtPrice}
								type="number"
								min={0}
								onChange={changeCompareAtPrice}
							/>
						</div>
					</div>
				</Modal.Section>

				<Modal.Section>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<div style={{ marginBottom: "0.6rem" }}>
							<Heading>{t("single.product_collections")}</Heading>
						</div>
						<div style={{ display: "flex", gap: "0.6rem" }}>
							<Button
								onClick={() => {
									setShowCollectionCreator(true)
									setShowCollectionPicker(false)
								}}
							>
								{t("single.create_collection")}
							</Button>
							<Button
								onClick={() => {
									setShowCollectionPicker(true)
									setShowCollectionCreator(false)
								}}
							>
								{t("single.select_collections")}
							</Button>
						</div>
					</div>
					<div style={{ marginTop: "1rem" }}>
						{showCollectionCreator ? (
							<CollectionCreator
								discard={() => setShowCollectionCreator(false)}
								onCollectionCreated={(newCollection) => {
									let alreadySelectedCollectionsIDs = []
									setShowCollectionCreator(false)
									setCollectionsCreated((prev) => prev + 1)
									setCollectionsSelected(
										[
											...collectionsSelected,
											newCollection,
										].filter((c) => {
											if (
												!alreadySelectedCollectionsIDs.includes(
													c.id,
												)
											) {
												alreadySelectedCollectionsIDs.push(
													c.id,
												)
												return true
											} else {
												return false
											}
										}),
									)
								}}
							/>
						) : collectionsSelected.length > 0 ? (
							<Banner
								status="info"
								title={`${t("single.product_will_be_added_to")} ${collectionsSelected.map((c) => c.title).join(", ")}`}
							></Banner>
						) : (
							<Banner
								status="info"
								title={t(
									"single.product_will_be_added_to_the_default_collection",
								)}
							></Banner>
						)}
					</div>

					<CollectionPicker
						open={showCollectionPicker}
						onCancel={() => setShowCollectionPicker(false)}
						onSelection={(selections) => {
							setCollectionsSelected(
								selections.map((selection) => {
									return {
										title: selection.title,
										id: selection.id,
									}
								}),
							)
						}}
					/>
				</Modal.Section>

				<Modal.Section>
					<div
						style={{
							marginBottom: "1rem",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Heading>{t("single.product_description")}</Heading>

						{descriptionIsLongEnough ? (
							<AIOrdersButtons
								size="large"
								inputType="description"
								disabled={!state.hasValidKopyPlusSubscription}
							/>
						) : (
							""
						)}
					</div>
					<DescriptionEditor
						initialHTML={product.body_html}
						onEdit={changeDescription}
					/>
				</Modal.Section>

				<Modal.Section>
					<div
						style={{
							marginBottom: "1rem",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Heading>
							{t("single.product_variants")} (
							{variants?.length || 0})
						</Heading>

						{hasEnoughVariants && !hasDefaultTitleVariant ? (
							<AIOrdersButtons
								size="large"
								inputType="variants"
								disabled={!state.hasValidKopyPlusSubscription}
							/>
						) : (
							""
						)}
					</div>
					<div style={{ display: "flex", gap: "1rem" }}>
						<div
							onClick={selectAllVariants}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.3rem",
								padding: "0.2rem 0.6rem",
								background: "#efefef",
								borderRadius: "0.5rem",
								cursor: "pointer",
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1rem"
								height="1rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M9 16.17L5.53 12.7a.996.996 0 1 0-1.41 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71a.996.996 0 1 0-1.41-1.41L9 16.17z"
								></path>
							</svg>
							<span style={{ fontSize: "0.8rem" }}>
								{t("single.select_all")}
							</span>
						</div>
						<div
							onClick={unselectAllVariants}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.3rem",
								padding: "0.2rem 0.6rem",
								background: "#efefef",
								borderRadius: "0.5rem",
								cursor: "pointer",
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="0.8rem"
								height="0.8rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 2c5.5 0 10 4.5 10 10s-4.5 10-10 10S2 17.5 2 12S6.5 2 12 2m0 2c-1.9 0-3.6.6-4.9 1.7l11.2 11.2c1-1.4 1.7-3.1 1.7-4.9c0-4.4-3.6-8-8-8m4.9 14.3L5.7 7.1C4.6 8.4 4 10.1 4 12c0 4.4 3.6 8 8 8c1.9 0 3.6-.6 4.9-1.7Z"
								></path>
							</svg>
							<span style={{ fontSize: "0.8rem" }}>
								{t("single.unselect_all")}
							</span>
						</div>
					</div>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "1rem",
							marginTop: "1rem",
						}}
					>
						{variants &&
							variants.length > 0 &&
							variants.map((variant, i) => {
								const imageSrc =
									variant.imageSrc ||
									product.images.filter(
										(img) => img.id === variant.image_id,
									)?.[0]?.src ||
									""

								return (
									<div
										key={`variant-${i}}`}
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "0.2rem",
											width: "calc(100% - 1rem)",
											borderLeft: "1px solid #ccc",
											paddingLeft: "0.8rem",
										}}
									>
										<ProductVariantCheckbox
											checked={selectedVariantsIDs.find(
												(sv) => sv === variant.id,
											)}
											key={`variant-checkbox-${i}`}
											id={variant.id}
											label={variant.title}
											onChange={changeSelectedVariants}
										/>
										<div
											style={{
												display: "flex",
												gap: "1rem",
											}}
										>
											<TextField
												label={t("single.price")}
												prefix={state.shopCurrency}
												value={variant.price}
												type="number"
												min={0}
												onChange={(e) =>
													changeVariantPrice(
														e,
														variant.id,
													)
												}
											/>
											<TextField
												label={t(
													"single.compare_at_price",
												)}
												prefix={state.shopCurrency}
												value={variant.compareAtPrice}
												type="number"
												min={0}
												onChange={(e) =>
													changeVariantCompareAtPrice(
														e,
														variant.id,
													)
												}
											/>
											{imageSrc && (
												<img
													referrerPolicy="no-referrer"
													className="shadow border"
													style={{
														height: "3.7rem",
														width: "3.7rem",
														borderRadius: "0.5rem",
														objectFit: "contain",
													}}
													src={imageSrc}
												/>
											)}
										</div>
									</div>
								)
							})}
					</div>
					{selectedVariantsIDs.length === 0 ? (
						<div style={{ marginTop: "1rem" }}>
							<Banner
								title={t("single.select_at_least_1_variant")}
								status="critical"
							/>
						</div>
					) : (
						""
					)}
				</Modal.Section>

				<Modal.Section>
					<div
						style={{
							marginBottom: "1rem",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Heading>{t("single.product_tags")}</Heading>
						<div
							onClick={removeAllTags}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.3rem",
								padding: "0.2rem 0.6rem",
								background: "#efefef",
								borderRadius: "0.5rem",
								cursor: "pointer",
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="0.8rem"
								height="0.8rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 2c5.5 0 10 4.5 10 10s-4.5 10-10 10S2 17.5 2 12S6.5 2 12 2m0 2c-1.9 0-3.6.6-4.9 1.7l11.2 11.2c1-1.4 1.7-3.1 1.7-4.9c0-4.4-3.6-8-8-8m4.9 14.3L5.7 7.1C4.6 8.4 4 10.1 4 12c0 4.4 3.6 8 8 8c1.9 0 3.6-.6 4.9-1.7Z"
								></path>
							</svg>
							<span style={{ fontSize: "0.8rem" }}>
								{t("single.remove_all")}
							</span>
						</div>
					</div>
					<p>{t("single.add_multiple_by_separating_with_a_comma")}</p>
					<FormLayout>
						<TextField
							value={tagString}
							onChange={(change) => setTagString(change)}
							connectedRight={
								<Button onClick={addTag}>
									{t("common.add")}
								</Button>
							}
						/>
						<Stack spacing="extraTight">
							{tags.map((tag) => (
								<Tag key={tag} onRemove={() => removeTag(tag)}>
									{tag}
								</Tag>
							))}
						</Stack>
					</FormLayout>
				</Modal.Section>
			</div>

			<ConfirmBeforeClosingModal
				opened={confirmBeforeClosingModalIsOpen}
				setOpened={setConfirmBeforeClosingModalIsOpen}
				onConfirmClose={props.onModalClose}
			/>

			{showUpdateScopesModal && (
				<div
					style={{
						position: "fixed",
						top: "0",
						left: "0",
						right: "0",
						bottom: "0",
						height: "100%",
						width: "100%",
						zIndex: "550",
						background: "rgba(0, 0, 0, 0.3)",
						backdropFilter: "blur(5px)",
						display: "flex",
						flexDirection: "column",
						gap: "1rem",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<div
						style={{
							background: "#fcfcfc",
							padding: "1.5rem",
							borderRadius: "1rem",
						}}
					>
						<ScopesUpdateTutorial
							context="publishedField"
							requiredScopes={["write_publications"]}
							hasNeccessaryScopes={state.currentScopes?.includes(
								"write_publications",
							)}
							onRequiredScopesChecked={() => {
								setPublishChecked(true)
								setShowUpdateScopesModal(false)
							}}
							videoSrc="https://kopy.b-cdn.net/kopy_publications_scopes_tutorial.mp4"
						/>
						<div style={{ marginTop: "1rem" }}>
							<Button
								onClick={() => setShowUpdateScopesModal(false)}
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			)}
		</Modal>
	)
}

export default SingleProductImportModal
